import {CheckIcon, XIcon, ClockIcon} from '@primer/octicons-react'
import type {CSSProperties} from 'react'
import {OPTIONAL_CHECKS_FAILING} from '../consts/inbox-consts'
import {reviewDecisionTooltipLabel, type ReviewDecisionCounts} from '../consts/review-decision-consts'
import {
  DeferredCategory,
  ReviewDecisionState,
  StatusCheckState,
  type CategoryDisplay,
  type StatusCheckRollup,
} from '../types/list-types'
import {getCategoryDisplay} from './category-helpers'

/**
 * Returns a composite category display when the server-assigned category
 * doesn't fully capture the PR state. Specifically, when a PR is categorized
 * as READY_TO_MERGE but its CI checks are failing, we override the display
 * to surface the failing-checks signal instead of the green "Ready to merge".
 * Uses the 'Able to Merge'
 */
export function getCompositeCategoryDisplay(
  category: DeferredCategory,
  statusCheckRollup?: StatusCheckRollup,
): CategoryDisplay {
  if (category === DeferredCategory.ReadyToMerge && statusCheckRollup?.state === StatusCheckState.Failure) {
    return OPTIONAL_CHECKS_FAILING
  }
  return getCategoryDisplay(category)
}

/**
 * Maps an inbox category to the review decision state it represents, so that the
 * category badge can reuse the review-decision tooltips from the pulls list view:
 *
 * - Awaiting approval (AWAITING_REVIEW / WAITING_FOR_REVIEW) → Review required
 * - Ready to merge / Able to merge (READY_TO_MERGE)          → Approved
 * - Changes requested (CHANGES_REQUESTED)                    → Changes requested
 *
 * All other categories have no corresponding review state.
 */
function categoryReviewState(category: DeferredCategory): ReviewDecisionState | undefined {
  switch (category) {
    case DeferredCategory.AwaitingReview:
    case DeferredCategory.WaitingForReview:
      return ReviewDecisionState.ReviewRequired
    case DeferredCategory.ReadyToMerge:
      return ReviewDecisionState.Approved
    case DeferredCategory.ChangesRequested:
      return ReviewDecisionState.ChangesRequested
    default:
      return undefined
  }
}

/**
 * Returns the review-decision tooltip label for an inbox category badge, or `undefined` when the
 * category has no associated review state.
 */
export function inboxReviewTooltipLabel(
  category: DeferredCategory,
  counts: ReviewDecisionCounts = {},
): string | undefined {
  return reviewDecisionTooltipLabel(categoryReviewState(category), counts)
}

const statusCheckIcon = (state: StatusCheckState, size: number = 16) => {
  switch (state) {
    case StatusCheckState.Success:
      return <CheckIcon size={size} className="color-fg-success" />
    case StatusCheckState.Failure:
      return <XIcon size={size} className="color-fg-danger" />
    case StatusCheckState.Pending:
      return <ClockIcon size={size} className="color-fg-attention" />
    default:
      return null
  }
}

const inlineFlexCenterGap: CSSProperties = {display: 'inline-flex', alignItems: 'center', gap: '4px'}

export const statusCheckColumnDisplay = (statusCheckRollup: StatusCheckRollup) => {
  const icon = statusCheckIcon(statusCheckRollup.state)
  if (!icon) return null

  return (
    <span data-testid="ci-status-column" style={inlineFlexCenterGap}>
      {icon} {statusCheckRollup.successCount}/{statusCheckRollup.totalCount}
    </span>
  )
}
